import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import LoadingSpinner from './common/LoadingSpinner';
import { DayOriginRect } from './WatchdogDayModal';
import WatchdogInsuranceOrdersModal, {
  InsuranceKind,
} from './WatchdogInsuranceOrdersModal';
import { formatRial } from '../utils/formatMoneyFa';

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

const MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

const TYPE_COLORS = [
  '#4f46e5',
  '#0891b2',
  '#059669',
  '#d97706',
  '#db2777',
  '#7c3aed',
  '#0d9488',
  '#ea580c',
];

export interface MonthDetailData {
  year: number;
  month: number;
  period: string;
  total: number;
  naghdi?: number;
  bime?: number;
  bime_paye?: number;
  bime_takmili?: number;
  count_paye?: number;
  count_takmili?: number;
  bime_done?: number;
  bime_waiting?: number;
  count_done?: number;
  count_waiting?: number;
  bime_paye_done?: number;
  bime_paye_waiting?: number;
  bime_takmili_done?: number;
  bime_takmili_waiting?: number;
  total_amount?: number;
  peak_day: string | null;
  peak_day_count: number;
  days: { day: number; date: string; count: number }[];
  types: {
    label: string;
    count: number;
    naghdi?: number;
    bime?: number;
    total_amount?: number;
  }[];
  services: { label: string; count: number }[];
}

interface Props {
  open: boolean;
  year: number | null;
  month: number | null;
  monthLabel?: string;
  origin: DayOriginRect | null;
  isCurrentMonth?: boolean;
  loading: boolean;
  error: string | null;
  data: MonthDetailData | null;
  serviceTypesQuery?: string;
  onClose: () => void;
}

type Phase = 'expanding' | 'open' | 'collapsing';

const WatchdogMonthModal: React.FC<Props> = ({
  open,
  year,
  month,
  monthLabel,
  origin,
  isCurrentMonth,
  loading,
  error,
  data,
  serviceTypesQuery,
  onClose,
}) => {
  const [phase, setPhase] = useState<Phase>('expanding');
  const [orbGrown, setOrbGrown] = useState(false);
  const [panelIn, setPanelIn] = useState(false);
  const [insuKind, setInsuKind] = useState<InsuranceKind | null>(null);

  const handleClose = () => {
    if (phase === 'collapsing') return;
    setPanelIn(false);
    setPhase('collapsing');
    window.setTimeout(() => setOrbGrown(false), 120);
    window.setTimeout(() => onClose(), 520);
  };

  useEffect(() => {
    if (!open) {
      setPhase('expanding');
      setOrbGrown(false);
      setPanelIn(false);
      return;
    }
    setPhase('expanding');
    setOrbGrown(false);
    setPanelIn(false);
    const grow = window.setTimeout(() => setOrbGrown(true), 16);
    const openPanel = window.setTimeout(() => {
      setPhase('open');
      setPanelIn(true);
    }, 380);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (insuKind) {
        setInsuKind(null);
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(grow);
      window.clearTimeout(openPanel);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, year, month, insuKind]);

  const dayMax = useMemo(
    () => Math.max(1, ...(data?.days || []).map((d) => d.count)),
    [data]
  );

  const typeTotal = useMemo(
    () => (data?.types || []).reduce((s, t) => s + t.count, 0) || 1,
    [data]
  );

  const moneyTotals = useMemo(() => {
    const naghdi = data?.naghdi ?? 0;
    const bime = data?.bime ?? 0;
    const bimePaye = data?.bime_paye ?? Math.max(0, bime - (data?.bime_takmili ?? 0));
    const bimeTakmili = data?.bime_takmili ?? 0;
    const bimeDone = data?.bime_done ?? 0;
    // جمع کل = نقدی + بیمه انجام‌شده
    const total = data?.total_amount ?? naghdi + bimeDone;
    const cashPct = total > 0 ? (naghdi / total) * 100 : 0;
    const insPct = total > 0 ? (bimeDone / total) * 100 : 0;
    const payePct = bime > 0 ? (bimePaye / bime) * 100 : 0;
    const takmiliPct = bime > 0 ? (bimeTakmili / bime) * 100 : 0;
    return {
      naghdi,
      bime,
      bimePaye,
      bimeTakmili,
      countPaye: data?.count_paye ?? 0,
      countTakmili: data?.count_takmili ?? 0,
      bimeDone,
      bimeWaiting: data?.bime_waiting ?? 0,
      countDone: data?.count_done ?? 0,
      countWaiting: data?.count_waiting ?? 0,
      bimePayeDone: data?.bime_paye_done ?? 0,
      bimePayeWaiting: data?.bime_paye_waiting ?? 0,
      bimeTakmiliDone: data?.bime_takmili_done ?? 0,
      bimeTakmiliWaiting: data?.bime_takmili_waiting ?? 0,
      total,
      cashPct,
      insPct,
      payePct,
      takmiliPct,
    };
  }, [data]);

  const monthBounds = useMemo(() => {
    if (!year || !month) return { from: '', to: '' };
    const from = `${year}/${String(month).padStart(2, '0')}/01`;
    const to = data?.days?.length
      ? data.days[data.days.length - 1]?.date || from
      : from;
    return { from, to };
  }, [year, month, data]);

  const activeDays = useMemo(() => {
    const rows = data?.days || [];
    const withData = rows.filter((d) => d.count > 0);
    if (!withData.length) return rows;
    const minD = Math.max(1, Math.min(...withData.map((d) => d.day)) - 1);
    const maxD = Math.max(...withData.map((d) => d.day)) + 1;
    return rows.filter((d) => d.day >= minD && d.day <= maxD);
  }, [data]);

  if (!open || year == null || month == null) return null;

  const title = `${MONTH_NAMES[month - 1] || ''} ${toPersianDigits(year)}`;
  const start = origin || {
    top: typeof window !== 'undefined' ? window.innerHeight / 2 - 16 : 0,
    left: typeof window !== 'undefined' ? window.innerWidth / 2 - 16 : 0,
    width: 32,
    height: 32,
  };
  const cx = start.left + start.width / 2;
  const cy = start.top + start.height / 2;
  const base = Math.max(start.width, start.height);
  const softScale = 1.85;
  const showOrb = phase !== 'open';

  return createPortal(
    <div className="fixed inset-0 z-[90]" style={{ direction: 'rtl' }}>
      <div
        className={`absolute inset-0 bg-slate-900/30 backdrop-blur-[1.5px] transition-opacity duration-500 ease-out ${
          orbGrown && phase !== 'collapsing' ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden
      />

      {showOrb ? (
        <div
          className="pointer-events-none fixed z-[91]"
          style={{
            top: cy,
            left: cx,
            width: base,
            height: base,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <span
            className={`absolute inset-0 rounded-2xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isCurrentMonth ? 'bg-emerald-400/25' : 'bg-indigo-400/25'
            }`}
            style={{
              transform: orbGrown ? `scale(${softScale + 0.9})` : 'scale(1)',
              opacity: orbGrown ? 0 : 0.55,
            }}
            aria-hidden
          />
          <div
            className={`relative flex h-full w-full items-center justify-center rounded-2xl px-1 text-[10px] font-bold transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isCurrentMonth
                ? 'bg-emerald-500 text-white'
                : 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white'
            }`}
            style={{
              transform: orbGrown ? `scale(${softScale})` : 'scale(1)',
              fontSize: orbGrown ? '0.75rem' : '0.55rem',
            }}
          >
            {monthLabel || MONTH_NAMES[month - 1]?.slice(0, 3)}
          </div>
        </div>
      ) : null}

      {(phase === 'open' || phase === 'collapsing') && (
        <div
          className="pointer-events-none absolute inset-0 z-[92] flex items-end justify-center p-3 sm:items-center sm:p-6"
          onClick={handleClose}
        >
          <div
            className={`pointer-events-auto relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl shadow-indigo-900/10 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              panelIn && phase === 'open'
                ? 'translate-y-0 scale-100 opacity-100'
                : 'translate-y-3 scale-[0.98] opacity-0'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="relative border-b border-gray-100 bg-gradient-to-l from-indigo-50 via-white to-white px-5 py-5 sm:px-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-indigo-600">جزئیات ماه</p>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                    {title}
                  </h2>
                  <p className="mt-1 text-sm tabular-nums text-gray-500">
                    {toPersianDigits(`${year}/${String(month).padStart(2, '0')}`)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  بستن
                </button>
              </div>

              {!loading && data ? (
                <>
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border border-indigo-100 bg-white px-4 py-3 shadow-sm">
                      <p className="text-[11px] text-gray-500">کل پذیرش</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
                        {toPersianDigits(data.total)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-emerald-100 bg-white px-4 py-3 shadow-sm">
                      <p className="text-[11px] text-gray-500">روز اوج</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-600">
                        {data.peak_day ? toPersianDigits(data.peak_day.split('/').pop() || '') : '—'}
                      </p>
                    </div>
                    <div className="col-span-2 rounded-xl border border-violet-100 bg-white px-4 py-3 shadow-sm sm:col-span-1">
                      <p className="text-[11px] text-gray-500">پذیرش در اوج</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-indigo-600">
                        {toPersianDigits(data.peak_day_count || 0)}
                      </p>
                    </div>
                  </div>

                  {moneyTotals.total > 0 ? (
                    <div className="mt-4 space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3">
                          <p className="text-[11px] text-amber-800/80" title="جمع دریافتی صندوق در این ماه، مطابق گزارش صندوق HIS">
                            مبلغ نقدی
                          </p>
                          <p className="mt-1 text-lg font-bold tabular-nums text-amber-900">
                            {formatRial(moneyTotals.naghdi)}
                          </p>
                        </div>
                        <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3">
                          <p
                            className="text-[11px] text-sky-800/80"
                            title="جمع سهم سازمان بیمه؛ تفکیک پایه (InsuA) و تکمیلی (InsuB)"
                          >
                            مبلغ بیمه
                          </p>
                          <p className="mt-1 text-lg font-bold tabular-nums text-sky-900">
                            {formatRial(moneyTotals.bime)}
                          </p>
                          <div className="mt-2.5 grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setInsuKind('paye')}
                              className="rounded-lg border border-indigo-200/80 bg-white/80 px-2.5 py-2 text-right transition hover:border-indigo-400 hover:bg-indigo-50"
                              title="کلیک برای دیدن لیست سفارش‌های بیمه پایه"
                            >
                              <p className="text-[10px] text-indigo-700/80">بیمه پایه</p>
                              <p className="mt-0.5 text-sm font-bold tabular-nums text-indigo-900">
                                {formatRial(moneyTotals.bimePaye)}
                              </p>
                              <p className="mt-0.5 text-[10px] leading-4 text-indigo-600/80">
                                انجام‌شده {formatRial(moneyTotals.bimePayeDone)}
                                <br />
                                در انتظار {formatRial(moneyTotals.bimePayeWaiting)}
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={() => setInsuKind('takmili')}
                              className="rounded-lg border border-cyan-200/80 bg-white/80 px-2.5 py-2 text-right transition hover:border-cyan-400 hover:bg-cyan-50"
                              title="کلیک برای دیدن لیست سفارش‌های بیمه تکمیلی"
                            >
                              <p className="text-[10px] text-cyan-700/80">بیمه تکمیلی</p>
                              <p className="mt-0.5 text-sm font-bold tabular-nums text-cyan-900">
                                {formatRial(moneyTotals.bimeTakmili)}
                              </p>
                              <p className="mt-0.5 text-[10px] leading-4 text-cyan-600/80">
                                انجام‌شده {formatRial(moneyTotals.bimeTakmiliDone)}
                                <br />
                                در انتظار {formatRial(moneyTotals.bimeTakmiliWaiting)}
                              </p>
                            </button>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                            <button
                              type="button"
                              onClick={() => setInsuKind('done')}
                              className="rounded-md bg-emerald-50/90 px-2 py-1.5 text-right text-emerald-800 ring-1 ring-emerald-100 transition hover:bg-emerald-100 hover:ring-emerald-300"
                              title="کلیک برای دیدن لیست سفارش‌های انجام‌شده"
                            >
                              <p>انجام‌شده</p>
                              <p className="mt-0.5 font-bold tabular-nums">
                                {formatRial(moneyTotals.bimeDone)}
                              </p>
                              <p className="opacity-80">
                                {toPersianDigits(moneyTotals.countDone)} پذیرش
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={() => setInsuKind('waiting')}
                              className="rounded-md bg-amber-50/90 px-2 py-1.5 text-right text-amber-900 ring-1 ring-amber-100 transition hover:bg-amber-100 hover:ring-amber-300"
                              title="کلیک برای دیدن لیست سفارش‌های در انتظار انجام"
                            >
                              <p>در انتظار انجام</p>
                              <p className="mt-0.5 font-bold tabular-nums">
                                {formatRial(moneyTotals.bimeWaiting)}
                              </p>
                              <p className="opacity-80">
                                {toPersianDigits(moneyTotals.countWaiting)} پذیرش
                              </p>
                            </button>
                          </div>
                          {moneyTotals.bime > 0 ? (
                            <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-white/70">
                              <div
                                className="bg-indigo-400"
                                style={{ width: `${Math.max(2, moneyTotals.payePct)}%` }}
                              />
                              <div
                                className="bg-cyan-400"
                                style={{ width: `${Math.max(2, moneyTotals.takmiliPct)}%` }}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                          <p
                            className="text-[11px] text-gray-600"
                            title="نقدی + بیمه انجام‌شده (در انتظار لحاظ نمی‌شود)"
                          >
                            جمع کل
                          </p>
                          <p className="mt-1 text-lg font-bold tabular-nums text-gray-900">
                            {formatRial(moneyTotals.total)}
                          </p>
                        </div>
                      </div>
                      <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="bg-gradient-to-l from-amber-500 to-amber-400"
                          style={{ width: `${Math.max(2, moneyTotals.cashPct)}%` }}
                        />
                        <div
                          className="bg-gradient-to-l from-sky-500 to-sky-400"
                          style={{ width: `${Math.max(2, moneyTotals.insPct)}%` }}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </header>

            <div className="relative flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              {loading ? (
                <div className="py-14">
                  <LoadingSpinner text="در حال آماده‌سازی جزئیات ماه..." />
                </div>
              ) : error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800">
                  {error}
                </div>
              ) : !data || data.total === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-12 text-center">
                  <p className="text-base font-semibold text-gray-800">پذیرشی ثبت نشده</p>
                  <p className="mt-2 text-sm text-gray-500">برای این ماه داده‌ای در HIS نیست.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-base font-bold text-gray-900">تایم‌لاین روزانه</h3>
                    <p className="mt-0.5 text-xs text-gray-500">شلوغی پذیرش در طول ماه</p>
                    <div className="mt-3 rounded-xl border border-gray-100 bg-gradient-to-b from-slate-50 to-white p-4">
                      <div className="flex items-end gap-0.5 sm:gap-1" style={{ height: 160 }}>
                        {activeDays.map((row) => {
                          const barH =
                            row.count <= 0
                              ? 4
                              : Math.max(10, Math.round((row.count / dayMax) * 132));
                          const isPeak = data.peak_day === row.date && row.count > 0;
                          return (
                            <div
                              key={row.date}
                              className="group relative flex h-full min-w-0 flex-1 flex-col items-center justify-end"
                              title={`${row.date} — ${row.count}`}
                            >
                              <span
                                className={`mb-1 text-[8px] font-semibold tabular-nums ${
                                  isPeak
                                    ? 'text-emerald-600'
                                    : 'text-gray-400 opacity-0 group-hover:opacity-100'
                                }`}
                              >
                                {toPersianDigits(row.count)}
                              </span>
                              <div
                                className={`w-[70%] max-w-[0.9rem] rounded-t-md transition-all duration-700 ease-out ${
                                  isPeak
                                    ? 'bg-gradient-to-t from-emerald-600 to-emerald-400'
                                    : row.count > 0
                                      ? 'bg-gradient-to-t from-indigo-600 to-indigo-400'
                                      : 'bg-gray-200'
                                }`}
                                style={{ height: `${barH}px` }}
                              />
                              <span
                                className={`mt-1 text-[8px] tabular-nums ${
                                  isPeak ? 'font-bold text-emerald-600' : 'text-gray-400'
                                }`}
                              >
                                {toPersianDigits(row.day)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-base font-bold text-gray-900">تفکیک نوع خدمت</h3>
                    <div className="mt-3 space-y-3">
                      {(data.types || []).map((item, idx) => {
                        const pct = (item.count / typeTotal) * 100;
                        const color = TYPE_COLORS[idx % TYPE_COLORS.length];
                        const typeTotalAmt =
                          item.total_amount ?? (item.naghdi ?? 0) + (item.bime ?? 0);
                        return (
                          <div key={item.label}>
                            <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="truncate font-medium text-gray-800">{item.label}</span>
                              </span>
                              <span className="shrink-0 tabular-nums text-gray-600">
                                {toPersianDigits(item.count)}
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.max(3, pct)}%`,
                                  background: `linear-gradient(90deg, ${color}, ${color}aa)`,
                                }}
                              />
                            </div>
                            {typeTotalAmt > 0 ? (
                              <p className="mt-1.5 text-[11px] text-gray-500">
                                نقدی {formatRial(item.naghdi ?? 0)} · بیمه{' '}
                                {formatRial(item.bime ?? 0)}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {(data.services || []).length > 0 ? (
                    <section>
                      <h3 className="text-base font-bold text-gray-900">خدمات پرتکرار این ماه</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {data.services.slice(0, 8).map((s, idx) => (
                          <span
                            key={s.label}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700"
                          >
                            <span
                              className="h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: TYPE_COLORS[idx % TYPE_COLORS.length] }}
                            />
                            <span className="max-w-[14rem] truncate">{s.label}</span>
                            <span className="tabular-nums text-gray-500">{toPersianDigits(s.count)}</span>
                          </span>
                        ))}
                      </div>
                    </section>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <WatchdogInsuranceOrdersModal
        open={Boolean(insuKind)}
        kind={insuKind}
        fromDate={monthBounds.from}
        toDate={monthBounds.to}
        title={monthLabel || (data?.period ? toPersianDigits(data.period) : undefined)}
        serviceTypesQuery={serviceTypesQuery}
        onClose={() => setInsuKind(null)}
      />
    </div>,
    document.body
  );
};

export default WatchdogMonthModal;
